import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Bot,
  User,
  Loader2,
  MessageCircle,
  CheckCircle,
  AlertCircle,
  Star,
  Download,
} from 'lucide-react';
import geminiService from '../../services/geminiService';
import { pdfService } from '../../services/pdfService';
import {
  SlotSchema,
  SlotUtils,
  FunctionTypes,
  createChatMessage,
  UsageLimit,
  ChatMessage,
} from '../../types/chatTypes';
import './ChatInterface.css';
import { useApi } from '../../contexts/ApiContext';
import { useWithTokenRefresh } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface ChatInterfaceProps {
  onClose: () => void;
  isInline?: boolean;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ onClose, isInline = false }) => {
  // States for slot management
  const [slots, setSlots] = useState<Record<string, string>>({});
  const [expectedSlotKey, setExpectedSlotKey] = useState<string | null>(null);
  const [conversationEnded, setConversationEnded] = useState(false);

  // States for messages and interface
  const [messages, setMessages] = useState<ChatMessage[]>([
    createChatMessage(
      'assistant',
      'Hello! I am your cloud cost optimization expert. I help users reduce their cloud expenses and choose the right services. Tell me what you want to optimize and I will ask you some questions to provide you with personalized recommendations based on industry best practices.'
    ),
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [inputDisabled, setInputDisabled] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const [isDeepMode, setIsDeepMode] = useState(false);

  // References
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();
  const { authState } = useAuth();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || inputDisabled) return;

    const userMessage = createChatMessage('user', inputMessage.trim());
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setStreamingMessage('');

    try {
      // Process user response if we're expecting a specific slot
      const updatedSlots = { ...slots };
      let nextSlotKey = expectedSlotKey;

      if (expectedSlotKey && !conversationEnded) {
        const userResponse = userMessage.content.toLowerCase();

        // Check if user says "I don't know"
        if (
          userResponse.includes("i don't know") ||
          userResponse.includes('no idea') ||
          userResponse.includes('not sure') ||
          userResponse.includes("don't know")
        ) {
          // Don't fill the slot, move to next
          nextSlotKey = SlotUtils.getNextEmptySlot(updatedSlots);
        } else {
          // Fill the slot with the response
          updatedSlots[expectedSlotKey] = userMessage.content;
          setSlots(updatedSlots);
          nextSlotKey = SlotUtils.getNextEmptySlot(updatedSlots);
        }

        setExpectedSlotKey(nextSlotKey);
      }

      // Send message to Gemini with streaming
      const response = await geminiService.generateStreamResponseWithSlots(
        userMessage.content,
        updatedSlots,
        messages,
        nextSlotKey,
        (chunk, fullText) => {
          setStreamingMessage(fullText);
        },
        isDeepMode ? { deep: true } : undefined
      );

      if (response.success) {
        const assistantMessage = createChatMessage(
          'assistant',
          response.message,
          response.functionCall
        );

        setMessages((prev) => [...prev, assistantMessage]);

        // Process function call
        if (response.functionCall) {
          handleFunctionCall(response.functionCall, updatedSlots);
        }

        // If it's a final recommendation, prepare an email draft for validation/sending
        if (response.functionCall?.name === FunctionTypes.RECOMMENDATION) {
          setEmailDraft(response.message);
        }
      } else {
        const errorMessage = createChatMessage('assistant', response.message, null, {
          isError: true,
        });
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      const errorMessage = createChatMessage(
        'assistant',
        'An unexpected error occurred. Please try again.',
        null,
        { isError: true }
      );
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setStreamingMessage('');
    }
  };

  // Process AI function calls
  const handleFunctionCall = (
    functionCall: { name: string; parameters: any },
    currentSlots: Record<string, string>
  ) => {
    switch (functionCall.name) {
      case FunctionTypes.ASK_FOR_DATA:
        // AI asks a question for a slot
        setExpectedSlotKey(functionCall.parameters.slotKey);
        break;

      case FunctionTypes.CLARIFY_REQUEST:
        // AI asks for clarification
        // Expected slot remains the same
        break;

      case FunctionTypes.SKIP_SLOT: {
        // AI accepts to skip the slot
        const nextSlot = SlotUtils.getNextEmptySlot(currentSlots);
        setExpectedSlotKey(nextSlot);
        break;
      }

      case FunctionTypes.RECOMMENDATION:
        // Final recommendation - end conversation
        setConversationEnded(true);
        setInputDisabled(true);
        setExpectedSlotKey(null);
        break;

      case FunctionTypes.LIMIT_REACHED:
        // Limit reached - end conversation
        setConversationEnded(true);
        setInputDisabled(true);
        setExpectedSlotKey(null);
        break;

      default:
        break;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Build a plain text transcript (fallback if no draft)
  const getTranscriptText = () => {
    return messages.map((m) => `${m.role.toUpperCase()}: ${m.content}`).join('\n');
  };

  // Send the email to support using user's contact email from AuthContext
  const handleSendToSupport = async () => {
    try {
      setIsSending(true);
      const email = authState?.user?.email || '';
      const subject = 'AI chat support request';
      const message = emailDraft || getTranscriptText();
      const formData = new FormData();
      formData.append('email', email);
      formData.append('subject', subject);
      formData.append('message', message);
      const resp = await withTokenRefresh(() => apiClient.sendFeedbackEmail(formData));
      const success = (resp as any)?.success === true;
      const feedback = success
        ? 'Thanks! Your message has been sent to our support team. We will get back to you soon.'
        : 'We could not send your message at this time. Please try again later.';
      setMessages((prev) => [
        ...prev,
        createChatMessage('assistant', feedback, null, { isError: !success }),
      ]);
      if (success) {
        setEmailDraft(null);
      }
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        createChatMessage(
          'assistant',
          'An error occurred while sending your message. Please try again later.',
          null,
          { isError: true }
        ),
      ]);
    } finally {
      setIsSending(false);
    }
  };

  // Get icon for function type
  const getFunctionIcon = (functionType: string) => {
    switch (functionType) {
      case FunctionTypes.ASK_FOR_DATA:
        return <MessageCircle size={16} />;
      case FunctionTypes.CLARIFY_REQUEST:
        return <AlertCircle size={16} />;
      case FunctionTypes.SKIP_SLOT:
        return <CheckCircle size={16} />;
      case FunctionTypes.RECOMMENDATION:
        return <Star size={16} />;
      case FunctionTypes.LIMIT_REACHED:
        return <AlertCircle size={16} />;
      default:
        return <Bot size={16} />;
    }
  };

  // Get input placeholder based on state
  const getInputPlaceholder = () => {
    if (conversationEnded) {
      return 'Conversation ended';
    }
    if (expectedSlotKey) {
      return `Answer the question or type "I don't know" to skip...`;
    }
    return 'Type your message...';
  };

  // Handle PDF download for recommendations
  const handleDownloadPDF = (message: ChatMessage) => {
    pdfService.generateRecommendationPDF({
      recommendation: message.content,
      userInfo: slots,
      timestamp: message.timestamp,
    });
  };

  // Check if message should show PDF download button
  const shouldShowPDFButton = (message: ChatMessage) => {
    // Show for explicit recommendation function calls
    if (message.functionCall?.name === FunctionTypes.RECOMMENDATION) {
      return true;
    }

    // Detect recommendation content based on keywords and structure
    const isRecommendationContent = (content: string) => {
      const lowerContent = content.toLowerCase();
      const hasRecommendationKeywords =
        lowerContent.includes('recommend') ||
        lowerContent.includes('suggest') ||
        lowerContent.includes('optimization') ||
        lowerContent.includes('strategy') ||
        lowerContent.includes('plan:') ||
        lowerContent.includes("here's a plan") ||
        lowerContent.includes('cost explorer') ||
        lowerContent.includes('lifecycle policies') ||
        (lowerContent.includes('aws') && lowerContent.includes('costs'));

      const hasStructuredContent =
        content.includes('**') || // Bold formatting
        content.includes('1.') || // Numbered lists
        content.includes('*') || // Bullet points
        content.split('\n').length > 10; // Multi-paragraph

      return hasRecommendationKeywords && hasStructuredContent && content.length > 500;
    };

    // Show for long assistant messages that look like recommendations
    if (
      message.role === 'assistant' &&
      !message.isError &&
      isRecommendationContent(message.content)
    ) {
      return true;
    }

    return false;
  };

  // Trigger deep analysis for more detailed recommendations
  const handleMoreDetails = async (message: ChatMessage) => {
    if (isLoading) return;
    try {
      setIsDeepMode(true);
      setConversationEnded(false);
      setInputDisabled(false);
      setIsLoading(true);
      setStreamingMessage('');

      const userKickoff = createChatMessage(
        'user',
        'I would like a deeper analysis with more detailed questions and a more comprehensive plan.'
      );
      setMessages((prev) => [...prev, userKickoff]);

      const nextSlotKey = SlotUtils.getNextEmptySlot(slots);

      const response = await geminiService.generateStreamResponseWithSlots(
        userKickoff.content,
        { ...slots },
        messages,
        nextSlotKey,
        (chunk, fullText) => setStreamingMessage(fullText),
        { deep: true }
      );

      if (response.success) {
        const assistantMessage = createChatMessage(
          'assistant',
          response.message,
          response.functionCall
        );
        setMessages((prev) => [...prev, assistantMessage]);
        if (response.functionCall) {
          handleFunctionCall(response.functionCall, { ...slots });
        }
        if (response.functionCall?.name === FunctionTypes.RECOMMENDATION) {
          setEmailDraft(response.message);
        }
      } else {
        const errorMessage = createChatMessage('assistant', response.message, null, {
          isError: true,
        });
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (e) {
      const errorMessage = createChatMessage(
        'assistant',
        'An unexpected error occurred during deep analysis. Please try again.',
        null,
        { isError: true }
      );
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setStreamingMessage('');
    }
  };

  return (
    <div className={`chat-interface ${isInline ? 'inline-mode' : ''}`}>
      {/* Header - only show in full screen mode */}
      {!isInline && (
        <div className="chat-header">
          <div className="header-content">
            <div className="header-left">
              <Bot size={24} className="header-icon" />
              <div>
                <h1 className="header-title">FlashbackAI</h1>
                <p className="header-subtitle">Storage Optimization Assistant</p>
              </div>
            </div>
            <button onClick={onClose} className="close-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="m18 6-12 12" />
                <path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Slot summary */}
      {Object.keys(slots).length > 0 && (
        <div className="slots-summary">
          <h3>Collected Information</h3>
          <div className="slots-grid">
            {Object.entries(SlotSchema).map(([key, slot]) => (
              <div key={key} className={`slot-item ${slots[key] ? 'filled' : 'empty'}`}>
                <span className="slot-label">{slot.label}:</span>
                <span className="slot-value">{slots[key] || 'Not provided'}</span>
              </div>
            ))}
          </div>
          <div className="progress-info">
            <span>Progress: {SlotUtils.getCompletionPercentage(slots)}%</span>
            {expectedSlotKey && (
              <span className="next-question">
                Next question: {SlotSchema[expectedSlotKey as keyof typeof SlotSchema]?.label}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="messages-container">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`message-wrapper ${
              message.role === 'user' ? 'user-message' : 'assistant-message'
            }`}
          >
            <div
              className={`message-content ${
                message.role === 'user' ? 'user-content' : 'assistant-content'
              }`}
            >
              {/* Avatar */}
              <div
                className={`avatar ${
                  message.role === 'user'
                    ? 'user-avatar'
                    : message.isError
                    ? 'error-avatar'
                    : 'assistant-avatar'
                }`}
              >
                {message.role === 'user' ? (
                  <User size={20} color="white" />
                ) : (
                  <Bot size={20} color="white" />
                )}
              </div>

              {/* Message bubble */}
              <div
                className={`message-bubble ${
                  message.role === 'user'
                    ? 'user-bubble'
                    : message.isError
                    ? 'error-bubble'
                    : 'assistant-bubble'
                }`}
              >
                {/* Function badge for assistant messages */}
                {message.role === 'assistant' && message.functionCall && (
                  <div className={`function-badge ${message.functionCall.name}`}>
                    {getFunctionIcon(message.functionCall.name)}
                    <span className="function-name">
                      {message.functionCall.name === FunctionTypes.ASK_FOR_DATA && 'Question'}
                      {message.functionCall.name === FunctionTypes.CLARIFY_REQUEST &&
                        'Clarification'}
                      {message.functionCall.name === FunctionTypes.SKIP_SLOT && 'Slot Skipped'}
                      {message.functionCall.name === FunctionTypes.RECOMMENDATION &&
                        'Recommendation'}
                      {message.functionCall.name === FunctionTypes.LIMIT_REACHED && 'Limit Reached'}
                    </span>
                  </div>
                )}

                {/* Show PDF button instead of text for recommendations */}
                {shouldShowPDFButton(message) ? (
                  <div className="pdf-recommendation-container">
                    <div className="pdf-recommendation-message">
                      <p>Your personalized optimization recommendations are ready!</p>
                    </div>
                    <div className="pdf-download-container" style={{ gap: 8, display: 'flex' }}>
                      <button
                        onClick={() => handleDownloadPDF(message)}
                        className="pdf-download-button"
                        title="Download recommendation as PDF"
                      >
                        <Download size={16} />
                        <span>Download PDF Report</span>
                      </button>
                      <button
                        onClick={() => handleMoreDetails(message)}
                        className="pdf-download-button"
                        title="Ask for deeper analysis"
                        style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}
                      >
                        <Star size={16} />
                        <span>More details</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="message-text markdown-body">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({ node, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || '');
                          const inline = !match && !String(children).includes('\n');
                          return !inline ? (
                            <SyntaxHighlighter
                              style={oneLight as any}
                              language={match?.[1] || 'text'}
                              PreTag="div"
                              customStyle={{ borderRadius: '8px', fontSize: '13px', margin: '8px 0' }}
                            >
                              {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                          ) : (
                            <code className="inline-code" {...props}>{children}</code>
                          );
                        },
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                )}

                <div
                  className={`message-time ${
                    message.role === 'user'
                      ? 'user-time'
                      : message.isError
                      ? 'error-time'
                      : 'assistant-time'
                  }`}
                >
                  {formatTime(message.timestamp)}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Streaming message */}
        {isLoading && streamingMessage && (
          <div className="message-wrapper assistant-message">
            <div className="message-content assistant-content">
              <div className="avatar assistant-avatar">
                <Bot size={20} color="white" />
              </div>
              <div className="message-bubble assistant-bubble">
                <div className="message-text markdown-body">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      code({ node, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        const inline = !match && !String(children).includes('\n');
                        return !inline ? (
                          <SyntaxHighlighter
                            style={oneLight as any}
                            language={match?.[1] || 'text'}
                            PreTag="div"
                            customStyle={{ borderRadius: '8px', fontSize: '13px', margin: '8px 0' }}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code className="inline-code" {...props}>{children}</code>
                        );
                      },
                    }}
                  >
                    {streamingMessage}
                  </ReactMarkdown>
                </div>
                <div className="typing-status">
                  <Loader2 size={12} className="animate-spin" />
                  <span>Typing...</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && !streamingMessage && (
          <div className="message-wrapper assistant-message">
            <div className="message-content assistant-content">
              <div className="avatar assistant-avatar">
                <Bot size={20} color="white" />
              </div>
              <div className="message-bubble assistant-bubble">
                <div className="loading-status">
                  <Loader2 size={16} className="animate-spin" />
                  <span>FlashbackAI is thinking...</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Summary validation and send-to-support action */}
      {emailDraft && (
        <div className="slots-summary" style={{ marginTop: 8 }}>
          <h3>Summary to send</h3>
          <div className="pdf-recommendation-container" style={{ padding: 12 }}>
            <pre
              className="whitespace-pre-wrap text-sm text-gray-700"
              style={{ whiteSpace: 'pre-wrap' }}
            >
              {emailDraft}
            </pre>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
              <button
                onClick={() => setEmailDraft(null)}
                className="close-button"
                disabled={isSending}
              >
                Edit
              </button>
              <button
                onClick={handleSendToSupport}
                className="pdf-download-button"
                disabled={isSending}
                title="Send this summary to support"
              >
                {isSending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle size={16} />
                )}
                <span style={{ marginLeft: 6 }}>{isSending ? 'Sending…' : 'Send to support'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="input-container">
        <div className="input-wrapper">
          <div className="textarea-container">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={getInputPlaceholder()}
              className="message-input"
              disabled={inputDisabled}
              rows={1}
              style={{
                minHeight: '48px',
                maxHeight: '120px',
              }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              }}
            />
          </div>
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading || inputDisabled}
            className="send-button"
          >
            {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
